import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../config/fbConfig';
import Logo from '../images/school_logo.png';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import 'jspdf-autotable';
import { useUserAuth } from "../context/UserAuthContext";
import {SCHOOL_NAME, SCHOOL_ADDRESS, SCHOOL_EMAIL, SCHOOL_PHONE, RESULT_COLOR_1, RESULT_COLOR_2 } from '../Variables.js';

const ClassResultsIndividual = () => {
  const { userData } = useUserAuth();
  const { sessionId, termId, classId, kidId } = useParams();
  const [classInfo, setClassInfo] = useState(null);
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionName, setSessionName] = useState('');
  const [termName, setTermName] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [formMasterName, setFormMasterName] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch class data
        const classRef = doc(db, 'classes', classId);
        const classSnap = await getDoc(classRef);
        if (!classSnap.exists()) throw new Error('Class not found');
        const classData = classSnap.data();
        setClassInfo(classData);

        // Fetch session name
        const sessionRef = doc(db, 'sessions', sessionId);
        const sessionSnap = await getDoc(sessionRef);
        setSessionName(sessionSnap.exists() ? sessionSnap.data().name : sessionId);

        // Fetch term name
        const termRef = doc(db, 'terms', termId);
        const termSnap = await getDoc(termRef);
        setTermName(termSnap.exists() ? termSnap.data().name : termId);

        // Fetch all subjects
        const allSubjectsSnap = await getDocs(collection(db, 'subjects'));
        const allSubjects = allSubjectsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // Fetch student data
        const studentDoc = await getDoc(doc(db, 'students', kidId));
        if (!studentDoc.exists()) throw new Error('Student not found');
        const studentData = studentDoc.data();
        if (!(studentData.enrollments || []).some(enr => enr.classId === classId)) {
          throw new Error('Student not enrolled in this class');
        }
        setStudent({ id: studentDoc.id, ...studentData });

        // Fetch all teachers
        const teachersSnap = await getDocs(collection(db, 'users'));
        const allTeachers = teachersSnap.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(user => ['Teacher', 'Cashier', 'Admin'].includes(user.status) && user.isActive === true);

        // Get form master's name
        let formMasterName = '';
        if (classData.formMaster && classData.formMaster !== 'N/A' && !classData.formMaster.includes('/')) {
          try {
            const formMasterRef = doc(db, 'users', classData.formMaster);
            const formMasterDoc = await getDoc(formMasterRef);
            if (formMasterDoc.exists()) {
              const data = formMasterDoc.data();
              formMasterName = `${data.firstName} ${data.lastName}`;
            }
          } catch (error) {
            console.warn('Invalid formMaster reference:', error);
            formMasterName = 'Not Assigned';  // Fallback display
          }
        } else {
          formMasterName = 'Not Assigned';  // Or '' if preferred
        }
        setFormMasterName(formMasterName);

        // Map subject data to class subjects with teacher names
        const subjects = (classData.subjects || [])
          .map(sub => {
            const full = allSubjects.find(s => s.id === sub.id);
            if (!full) return null; // Skip if subject doesn't exist in collection
            const assignedTeachers = allTeachers.filter(teacher =>
              teacher.teachingAssignments?.some(assignment =>
                assignment.classId === classId && assignment.subjectId === sub.id
              )
            );

            return {
              ...sub,
              name: full.name, // No fallback needed since we checked full exists
              assignedTeachers: assignedTeachers.map(t => `${t.firstName} ${t.lastName}`).join(', ')
            };
          })
          .filter(Boolean); // Remove nulls
        setSubjects(subjects);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [classId, sessionId, termId, kidId]);

  const getScore = (student, subjectId, type) => {
    const enrollment = (student.enrollments || []).find(enr => enr.classId === classId);
    if (!enrollment) return '-';
    const subj = (enrollment.subjects || []).find(sub => sub.subjectId === subjectId);
    return subj?.scores?.[type] ?? '-';
  };

  // Updated function to check if student was absent
  const isAbsent = (student, subjectId, type) => {
    const enrollment = (student.enrollments || []).find(enr => enr.classId === classId);
    if (!enrollment) return false;

    const subj = (enrollment.subjects || []).find(sub => sub.subjectId === subjectId);
    return subj?.scores?.[`${type}Absent`] ?? false;
  };

  const getTotal = (student, subjectId) => {
    const first = parseFloat(getScore(student, subjectId, 'firstca')) || 0;
    const second = parseFloat(getScore(student, subjectId, 'secondca')) || 0;
    const exam = parseFloat(getScore(student, subjectId, 'exam')) || 0;
    return first + second + exam;
  };

  const getGrade = (total) => {
    if (total >= 70) return 'A';
    if (total >= 60) return 'B';
    if (total >= 50) return 'C';
    if (total >= 45) return 'D';
    if (total >= 40) return 'E';
    return 'F';
  };

  const getComment = (total) => {
    if (total >= 70) return 'An excellent result. Keep it up!';
    if (total >= 60) return 'A very good result. Try harder next term.';
    if (total >= 50) return 'A good result. You can do better.';
    if (total >= 45) return 'A fair result. Do better on your weak subjects.';
    if (total >= 40) return 'A fair result. Improve on your studies and focus in class.';
    return 'You can do much better. Seek help from your teachers or parents regularly.';
  };

  const getStudentName = (student) =>
    `${student.firstName} ${student.lastName} ${student.middleName || ''}`;

  const getPsychomotorSkills = (student) => {
    const enrollment = (student.enrollments || []).find(enr => enr.classId === classId);
    return enrollment?.psychomotorSkills || {};
  };

  const getOrdinal = (n) => {
    if (isNaN(n)) return '';
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  const generateStudentResultPDF = () => {
    if (!classInfo || !student || !subjects || subjects.length === 0) return;

    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm'
    });

    // Calculate student stats
    let grandTotal = 0;
    const subjectResults = {};

    subjects.forEach(subject => {
      const firstCA = parseFloat(getScore(student, subject.id, 'firstca')) || 0;
      const secondCA = parseFloat(getScore(student, subject.id, 'secondca')) || 0;
      const exam = parseFloat(getScore(student, subject.id, 'exam')) || 0;
      const total = firstCA + secondCA + exam;
      subjectResults[subject.id] = {
        firstCA,
        secondCA,
        exam,
        total,
        grade: getGrade(total),
        firstCAAbsent: isAbsent(student, subject.id, 'firstca'),
        secondCAAbsent: isAbsent(student, subject.id, 'secondca'),
        examAbsent: isAbsent(student, subject.id, 'exam')
      };
      grandTotal += total;
    });

    const average = subjects.length > 0 ? grandTotal / subjects.length : 0;
    const studentStats = {
      ...student,
      subjectResults,
      grandTotal,
      average: parseFloat(average.toFixed(2)),
      grandGrade: getGrade(average),
      comment: getComment(average)
    };

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;

    // Header
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...RESULT_COLOR_1);
    doc.text(`${SCHOOL_NAME}`.toUpperCase(), pageWidth/2, 20, { align: 'center' });

    // Logo
    const logoX = 14;
    const logoY = 13;
    const logoWidth = 22;
    const logoHeight = 22;
    doc.addImage(Logo, 'PNG', logoX, logoY, logoWidth, logoHeight);

    // School details
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(`${SCHOOL_ADDRESS}`, pageWidth/2, 27, { align: 'center' });
    doc.text(`${SCHOOL_PHONE} | ${SCHOOL_EMAIL}`, pageWidth/2, 32, { align: 'center' });

    // Divider line
    doc.setDrawColor(0, 0, 128);
    doc.setLineWidth(0.5);
    doc.line(margin, 37, pageWidth - margin, 37);

    // Report title
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 128);
    doc.text('STUDENT REPORT SHEET', pageWidth/2, 45, { align: 'center' });

    // Student details
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(`Name: ${getStudentName(student)}`, margin, 55);
    doc.text(`Admission No: ${student.admissionNumber || 'N/A'}`, margin, 62);
    doc.text(`Class: ${classInfo.name}`, pageWidth/2, 55);
    doc.text(`Session: ${sessionName}`, pageWidth/2, 62);
    doc.text(`Term: ${termName}`, pageWidth - margin, 55, { align: 'right' });

    // Performance summary
    doc.setFillColor(240, 240, 240);
    doc.roundedRect(margin, 65, pageWidth - 2*margin, 15, 3, 3, 'F');
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    
    doc.text(`Total Score: ${studentStats.grandTotal}`, margin + 10, 73);
    doc.text(`Average Score: ${studentStats.average}`, pageWidth/2 - 14, 73);
    doc.text(`Overall Grade: ${studentStats.grandGrade}`, pageWidth - margin - 50, 73);

    // === Calculate dynamic row height to fill fixed table height ===
    const tableStartY = 84;
    const tableEndY = pageHeight - 95; // Reserve space for comments and signatures
    const availableTableHeight = tableEndY - tableStartY;
    const numberOfRows = subjects.length;
    const dynamicRowHeight = availableTableHeight / (numberOfRows + 1); // +1 for header row

    const availableWidth = pageWidth - (margin * 2);
    
    // Academic performance table with dynamic row height
    autoTable(doc, {
      startY: tableStartY,
      head: [['S/N', 'Subject', '1st CA', '2nd CA', 'Exam', 'Total', 'Grade']],
      body: subjects.map((subject, index) => {
        const result = studentStats.subjectResults[subject.id];
        
        // Display "ABS" for absent scores
        const firstCADisplay = result.firstCAAbsent ? 'ABS' : result.firstCA;
        const secondCADisplay = result.secondCAAbsent ? 'ABS' : result.secondCA;
        const examDisplay = result.examAbsent ? 'ABS' : result.exam;
        
        return [
          index + 1,
          subject.name,
          firstCADisplay,
          secondCADisplay,
          examDisplay,
          result.total,
          result.grade
        ];
      }),
      headStyles: {
        fillColor: RESULT_COLOR_2,
        textColor: [255, 255, 255],
        fontSize: 9,
        halign: 'center',
        valign: 'middle'
      },
      bodyStyles: {
        fontSize: 9,
        textColor: [0, 0, 0],
        minCellHeight: dynamicRowHeight,
        valign: 'middle'
      },
      alternateRowStyles: {
        fillColor: [240, 240, 240]
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: availableWidth * 0.07 }, // S/N
        1: { halign: 'left', cellWidth: availableWidth * 0.33 },   // Subject
        2: { halign: 'center', cellWidth: availableWidth * 0.12 }, // 1st CA
        3: { halign: 'center', cellWidth: availableWidth * 0.12 }, // 2nd CA
        4: { halign: 'center', cellWidth: availableWidth * 0.12 }, // Exam
        5: { halign: 'center', cellWidth: availableWidth * 0.12 }, // Total
        6: { halign: 'center', cellWidth: availableWidth * 0.12 }  // Grade
      },
      margin: { left: margin, right: margin },
      styles: {
        lineWidth: 0.1,
        lineColor: [255, 255, 255],
        cellPadding: 3
      },
      tableWidth: 'wrap'
    });

    // Calculate dynamic height for psychomotor table based on remaining space
    const psychomotorStartY = doc.lastAutoTable.finalY + 8;
    const psychomotorEndY = pageHeight - 45; // Space for comments and signatures
    const availablePsychomotorHeight = psychomotorEndY - psychomotorStartY;
    const psychomotorRowHeight = Math.min(availablePsychomotorHeight / 6, 8); // 6 rows including header

    // Psychomotor skills table with dynamic height
    const psychomotorSkills = getPsychomotorSkills(student);
    autoTable(doc, {
      startY: psychomotorStartY,
      head: [
        [
          {
            content: 'PSYCHOMOTOR SKILLS ASSESSMENT',
            colSpan: 5,
            styles: {
              halign: 'center',
              fillColor: RESULT_COLOR_2,
              textColor: [255, 255, 255],
              fontSize: 10
            }
          }
        ],
        [
          { content: 'Skill', styles: { halign: 'left' } },
          { content: 'Rating', styles: { halign: 'center' } },
          { content: 'Skill', styles: { halign: 'left' } },
          { content: 'Rating', styles: { halign: 'center' } },
          { content: 'Grading Key', styles: { halign: 'center' } }
        ]
      ],
      body: [
        ['Handwriting', psychomotorSkills.handwriting || 'A', 'Politeness', psychomotorSkills.politeness || 'A', 'A - Excellent'],
        ['Verbal Fluency', psychomotorSkills.fluency || 'A', 'Cooperation', psychomotorSkills.cooperation || 'A', 'B - Very Good'],
        ['Games & Sports', psychomotorSkills.games || 'A', 'Leadership', psychomotorSkills.leadership || 'A', 'C - Satisfactory'],
        ['Punctuality', psychomotorSkills.punctuality || 'A', 'Attentiveness', psychomotorSkills.attentiveness || 'A', 'D - Fairly Good'],
        ['Neatness', psychomotorSkills.neatness || 'A', 'Honesty', psychomotorSkills.honesty || 'A', 'E - Needs Improvement']
      ],
      headStyles: {
        fillColor: [240, 240, 240],
        textColor: [0, 0, 0],
        fontSize: 8,
        lineWidth: 0.3,
        minCellHeight: psychomotorRowHeight
      },
      bodyStyles: {
        fontSize: 8,
        textColor: [0, 0, 0],
        lineWidth: 0.2,
        lineColor: [200, 200, 200],
        cellPadding: { top: 2, right: 1, bottom: 2, left: 2 },
        minCellHeight: psychomotorRowHeight
      },
      columnStyles: {
        0: { cellWidth: '25%', halign: 'left' },
        1: { cellWidth: '15%', halign: 'center' },
        2: { cellWidth: '25%', halign: 'left' },
        3: { cellWidth: '15%', halign: 'center' },
        4: { cellWidth: '20%', halign: 'center' }
      },
      margin: { left: margin, right: margin },
      styles: {
        lineWidth: 0.2,
        lineColor: [200, 200, 200]
      },
      tableWidth: 'auto'
    });

    // Position comments and signatures dynamically based on content
    const finalY = doc.lastAutoTable.finalY + 10;
    
    // Comments and signatures
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Comment: ', margin, finalY);
    doc.setFont('helvetica', 'normal');
    
    // Split comment into multiple lines if too long
    const comment = studentStats.comment;
    const maxCommentWidth = pageWidth - margin * 2 - 20;
    const commentLines = doc.splitTextToSize(comment, maxCommentWidth);
    doc.text(commentLines, margin + 20, finalY);

    // Calculate position for signatures based on comment height
    const signatureY = finalY + (commentLines.length * 4) + 8;

    doc.setFont('helvetica', 'bold');
    doc.text('Class Teacher: ', margin, signatureY);
    doc.setFont('helvetica', 'normal');
    doc.text(formMasterName || '________________________', margin + 28, signatureY);

    doc.setFont('helvetica', 'bold');
    const dateTextWidth = doc.getTextWidth('Date: ');
    doc.text("Principal's Sign/Date: ", pageWidth - margin - dateTextWidth - 77, signatureY);
    doc.setFont('helvetica', 'normal');
    doc.text('______________________', pageWidth - margin, signatureY, { align: 'right' });

    // Footer
    const now = new Date();
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };
    const formattedDate = now.toLocaleDateString('en-US', options);
    doc.setFontSize(7);
    doc.setTextColor(100, 100, 100);
    doc.text(`Printed on ${formattedDate}`, pageWidth/2, pageHeight - 7, { align: 'center' });

    doc.save(`${getStudentName(student)}_Result_${sessionName}_${termName}.pdf`);
  };

  if (loading) {
    return (
      <div className='scroll-fixed' style={{ minHeight: '100vh', padding: '20px' }}>
        <p className='male-female-stat'>Loading results...</p>
      </div>
    );
  }

  if (!student) {
    return (
      <div className='scroll-fixed' style={{ minHeight: '100vh', padding: '20px' }}>
        <p className='male-female-stat'>No student data available</p>
      </div>
    );
  }

  return (
    <div className='scroll-fixed' style={{ minHeight: '100vh' }}>
      <div className="top-elements row align-center space-between">
        <button onClick={() => navigate(-1)} className="back-link back-link1">
          <i className="fa-solid fa-arrow-left size3"></i>
        </button>
        <div className="column align-center justify-center">
          <img src={Logo} className="middle-logo" alt="logo" />
        </div>
        <Link to="#" className="home-link" onClick={() => window.location.reload()}>
          <i className="fa-solid fa-rotate-right size4"></i>
        </Link>
      </div>
      <div className='scroll-fixed'>    
        <p className='text-center size4 bold class-results-heading' style={{marginTop: "100px"}}>
          {getStudentName(student)}'s Results <br /><span className='size5'>({sessionName} - {termName} Term)</span>
        </p>
        {(userData?.status === 'Admin' || userData?.status === 'Cashier' || userData?.status === 'Parent') && (
          <div className='align-center space-around class-results-buttons'>
            <button className='list-btn bold' onClick={generateStudentResultPDF}>
              Print Result
            </button>
          </div>
        )}
      </div>

      <div className='scroll-fixed scroll-fixed1'>
        <div className='scrollable'>
          <div style={{ padding: '0', overflowX: 'auto', maxHeight: '80vh', marginTop: '0px' }}>
            {(() => {
              let grandTotal = 0;
              const subjectResults = {};

              subjects.forEach(subject => {
                const firstCA = parseFloat(getScore(student, subject.id, 'firstca')) || 0;
                const secondCA = parseFloat(getScore(student, subject.id, 'secondca')) || 0;
                const exam = parseFloat(getScore(student, subject.id, 'exam')) || 0;
                const total = firstCA + secondCA + exam;
                subjectResults[subject.id] = { 
                  firstCA, 
                  secondCA, 
                  exam, 
                  total, 
                  grade: getGrade(total),
                  firstCAAbsent: isAbsent(student, subject.id, 'firstca'),
                  secondCAAbsent: isAbsent(student, subject.id, 'secondca'),
                  examAbsent: isAbsent(student, subject.id, 'exam')
                };
                grandTotal += total;
              });

              const average = subjects.length > 0 ? grandTotal / subjects.length : 0;
              const psychomotor = getPsychomotorSkills(student);

              return (
                <table
                  className='male-female-stat'
                  border="1"
                  cellPadding="8"
                  style={{
                    borderCollapse: 'collapse',
                    minWidth: '100%',
                    marginBottom: '30px',
                    textAlign: 'center',
                  }}
                >
                  <thead>
                    <tr>
                      <th>S/N</th>
                      <th style={{ minWidth: '200px' }}>Subject</th>
                      <th>1st CA</th>
                      <th>2nd CA</th>
                      <th>Exam</th>
                      <th>Total</th>
                      <th>Grade</th>
                      <th colSpan="10">Psychomotor Skills</th>
                      <th>Total</th>
                      <th>Average</th>
                      <th>Grade</th>
                    </tr>
                    <tr>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th></th>
                      <th>H/W</th>
                      <th>Fluent</th>
                      <th>Games</th>
                      <th>Punct</th>
                      <th>Neat</th>
                      <th>Polite</th>
                      <th>Coperate</th>
                      <th>Lead</th>
                      <th>Attention</th>
                      <th>Honest</th>
                      <th></th>
                      <th></th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody className='result-table'>
                    {subjects.map((subject, index) => {
                      const result = subjectResults[subject.id];
                      
                      // Display "ABS" for absent scores with red color
                      const firstCADisplay = result.firstCAAbsent ? 
                        <span style={{ color: 'red', fontWeight: 'bold' }}>ABS</span> : result.firstCA;
                      const secondCADisplay = result.secondCAAbsent ? 
                        <span style={{ color: 'red', fontWeight: 'bold' }}>ABS</span> : result.secondCA;
                      const examDisplay = result.examAbsent ? 
                        <span style={{ color: 'red', fontWeight: 'bold' }}>ABS</span> : result.exam;
                      
                      return (
                        <tr key={`${student.id}-${subject.id}`}>
                          <td>{index + 1}</td>
                          <td style={{ minWidth: '200px', textAlign: 'left' }}>{subject.name}</td>
                          <td>{firstCADisplay}</td>
                          <td>{secondCADisplay}</td>
                          <td>{examDisplay}</td>
                          <td style={{ fontWeight: 'bold' }}>{result.total}</td>
                          <td style={{ fontWeight: 'bold', color: result.grade === 'F' ? 'red' : 'inherit' }}>{result.grade}</td>
                          {index === 0 && (
                            <>
                              <td rowSpan={subjects.length}>{psychomotor.handwriting || 'A'}</td>
                              <td rowSpan={subjects.length}>{psychomotor.fluency || 'A'}</td>
                              <td rowSpan={subjects.length}>{psychomotor.games || 'A'}</td>
                              <td rowSpan={subjects.length}>{psychomotor.punctuality || 'A'}</td>
                              <td rowSpan={subjects.length}>{psychomotor.neatness || 'A'}</td>
                              <td rowSpan={subjects.length}>{psychomotor.politeness || 'A'}</td>
                              <td rowSpan={subjects.length}>{psychomotor.cooperation || 'A'}</td>
                              <td rowSpan={subjects.length}>{psychomotor.leadership || 'A'}</td>
                              <td rowSpan={subjects.length}>{psychomotor.attentiveness || 'A'}</td>
                              <td rowSpan={subjects.length}>{psychomotor.honesty || 'A'}</td>
                              <td rowSpan={subjects.length} style={{ fontWeight: 'bold' }}>{grandTotal}</td>
                              <td rowSpan={subjects.length} style={{ fontWeight: 'bold' }}>{average.toFixed(2)}</td>
                              <td rowSpan={subjects.length} style={{ fontWeight: 'bold', color: getGrade(average) === 'F' ? 'red' : 'inherit' }}>{getGrade(average)}</td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassResultsIndividual;